import { useMutation } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useEffect, useRef } from 'react';

type TrackingParams = {
  userAgent: string;
  eventType: string;
  fbc?: string;
  ttclid?: string;
  platform?: 'facebook' | 'tiktok';
};

import { Loader } from '../../components/_atoms/_Loader';
import { Center } from '../../components/_atoms/Center';
import { NextPageWithLayout } from '../../components/layouts';
import BackendService from '../../services/backendService/backendService';
import {
  convertFbclidToFbc,
  getFbclid,
  getTtclid,
  getUserAgent,
} from '../../utils/adsConversionTrackingUtils';
import { getBaseUrl } from '../../utils/client';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';

/**
 * Due to Plausible. This page exists to track conversion rate when signing up.
 */
const SignupSuccess: NextPageWithLayout = () => {
  const router = useRouter();
  const ref = useRef<NodeJS.Timeout | undefined>(undefined);
  const { data: session } = useSession();

  // Use mutation for conversion tracking (both Facebook and TikTok)
  const { mutate: trackConversion } = useMutation({
    mutationFn: BackendService.instance.marketing.trackConversion,
    onError: error => {
      console.error('Error tracking conversion:', error);
    },
  });

  useEffect(() => {
    // Wait for session to be loaded before tracking conversion
    if (!session?.idToken) {
      return;
    }

    // Get tracking parameters from URL or localStorage (e.g. if callback URL was stripped)
    const fbclid = getFbclid();
    const ttclid = getTtclid();

    // Track conversion if any tracking parameter is present
    if (fbclid || ttclid) {
      const userAgent = getUserAgent();

      const trackingParams: TrackingParams = {
        userAgent,
        eventType: 'CompleteRegistration',
      };

      if (fbclid) {
        trackingParams.fbc = convertFbclidToFbc(fbclid);
        trackingParams.platform = 'facebook';
      }

      if (ttclid) {
        trackingParams.ttclid = ttclid;
        trackingParams.platform = 'tiktok';
      }

      // If both are present, prioritize Facebook for now (can be changed based on business logic)
      if (fbclid && ttclid) {
        trackingParams.platform = 'facebook';
      }

      trackConversion(trackingParams);
    }

    if (router) {
      const callbackURI = router.query.callbackURI as string | undefined;
      if (callbackURI) {
        router.replace(callbackURI);
      } else {
        router.replace('/');
      }
      return;
    }
    // A timeout that makes sure the user is not stuck on this site
    if (!ref.current) {
      ref.current = setTimeout(() => {
        window.location.replace(`${getBaseUrl()}/`);
      }, 10000);
    }
    return () => {
      if (ref.current) clearTimeout(ref.current);
    };
  }, [router, trackConversion, session]);
  return (
    <Center style={{ width: '100%', height: '100%' }}>
      <Loader />
    </Center>
  );
};
export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );
  return {
    props: {
      ...(await detectAndBestEffortSetLanguage(context)),
    },
  };
};
export default SignupSuccess;
